import React, { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { useMutation } from "@tanstack/react-query";
import { userMutations } from "@surgia/api/src/queryStore/shared/user";
import { logout } from "../store/auth";
const SignOut: React.FC = () => {
  const auth = useAuth();
  const authState = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [signOutStep, setSignOutStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const tenant_id = authState.globalState.tenant_id;
  const selectedBranch = authState.globalState.last_accessed_branch_id;

  const { mutateAsync: setLastAccessedBranch } = useMutation(
    userMutations.setLastAccessedBranch()
  );

  const signOutSteps = [
    "Saving your progress...",
    "Updating last accessed branch...",
    "Clearing session data...",
    "Redirecting to login...",
  ];

  useEffect(() => {
    const performSignOut = async () => {
      try {
        // Step 1: Save progress
        setSignOutStep(0);
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Step 2: Update last accessed branch
        if (
          selectedBranch &&
          tenant_id &&
          import.meta.env.VITE_PUBLIC_COMPONENT_ID
        ) {
          setSignOutStep(1);
          await setLastAccessedBranch({
            branchId: selectedBranch,
            tenantId: tenant_id,
            componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
          });
          await new Promise((resolve) => setTimeout(resolve, 600));
        }

        // Step 3: Clear session data
        setSignOutStep(2);
        const authChannel = new BroadcastChannel("auth-sync");
        authChannel.postMessage({ type: "LOGOUT" });
        authChannel.close();
        localStorage.clear();
        sessionStorage.clear();
        auth.removeUser();
        dispatch(logout());

        await new Promise((resolve) => setTimeout(resolve, 400));

        // Step 4: Redirect to login
        setSignOutStep(3);
        setIsComplete(true); // Mark as complete to keep progress at 100%
        await new Promise((resolve) => setTimeout(resolve, 300));

        auth.signoutRedirect();
      } catch (error) {
        console.error("Error during sign out:", error);
        // Even if there's an error, proceed with sign out
        setSignOutStep(2);
        localStorage.clear();
        sessionStorage.clear();
        setIsComplete(true); // Mark as complete even on error
        await new Promise((resolve) => setTimeout(resolve, 500));
        auth.signoutRedirect();
      }
    };

    performSignOut();
  }, [auth, selectedBranch, tenant_id, setLastAccessedBranch]);

  // Calculate progress percentage, ensuring it stays at 100% when complete
  const progressPercentage = isComplete
    ? 100
    : ((signOutStep + 1) / signOutSteps.length) * 100;

  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="text-center max-w-md mx-auto p-8">
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Signing Out</h1>
        <p className="text-gray-600 mb-8">
          Please wait while we securely sign you out of your account.
        </p>

        {/* Current step text */}
        <p className="text-sm text-gray-600 animate-pulse mb-6">
          {signOutSteps[signOutStep]}
        </p>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mt-6">
          <div
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${progressPercentage}%`,
            }}
          />
        </div>

        {/* Additional info */}
        <div className="mt-6 text-xs text-gray-500">
          <p>
            This process ensures your data is safely saved before signing out.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignOut;
